<template>
    <v-sheet v-if="title" rounded :color="color" class="route-number" :class="cssClasses">
        <div class="route-number__title">
            <span v-for="t in titleParts" :key="t">{{ t }}</span>
        </div>
    </v-sheet>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
    title?: string;
    color?: string;
    fixedSize?: boolean;
}

const props = defineProps<Props>();
const cssClasses = computed(() => [
    { 'route-number--fixed-size': props.fixedSize },
    { 'route-number--multiline': isMultiline.value },
    sizeClass.value
]);
const breakableRegex = /[a-zA-Z]+|\d+/gi;

const titleParts = computed(() => {
    let t = props.title ?? '';
    if (!props.fixedSize || t.length < 4) return [t];

    const matches = t.match(breakableRegex);
    if (!matches) return [t];

    return Array.from(matches).reduce<string[]>((acc, curr) => {
        acc.push(curr);

        return acc;
    }, []);
});

// condition: multiple letters and more than 2 numbers
// i.e. VY190, SK4512 but not N280, FB40
const isMultiline = computed(() => titleParts.value.length > 1 && titleParts.value[0].length > 1 && titleParts.value[1].length > 2);

const sizeClass = computed(() => {
    // if not fixed size, or length is too short, use default font size
    if (!props.fixedSize || (props.title || '').length <= 2) return undefined;

    // multi-line
    if (isMultiline.value) return 'route-number--font-xs';

    // 3-characters
    if ((props.title || '').length == 3) return 'route-number--font-sm';

    // 4-characters + 1 letter i.e. N280
    if ((props.title || '').length == 4 && titleParts.value[0].length === 1) return 'route-number--font-xxs';

    // everything else
    return 'route-number--font-xs';
});
</script>

<style lang="scss">
@use 'vuetify/settings' as *;
@use 'vuetify/lib/styles/tools/_index' as *;

.route-number {
    --route-number-color: var(--v-color-white);
    --route-number-bg-color: var(--v-color-black);
    --route-number-font-size: #{calc(14px / $font-size-root) * 1rem};

    padding: 2px 6px;
    color: var(--route-number-color);
    display: inline-flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: center;
    background-color: var(--route-number-bg-color);
    height: 24px;
    min-width: 30px;
    vertical-align: middle;

    &--fixed-size {
        padding-left: 0;
        padding-right: 0;
        width: 30px;
    }

    &__title {
        display: inline-flex;
        flex-wrap: nowrap;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: var(--route-number-font-size);
        line-height: 1;
        text-transform: uppercase;

        .route-number--fixed-size & {
            flex-wrap: wrap;
        }

        > span {
            // fixes some vertical alignment issue
            line-height: 24px;

            .route-number--multiline & {
                line-height: 1;
            }
        }
    }

    &--font-sm {
        --route-number-font-size: #{calc(12px / $font-size-root) * 1rem} !important;
    }

    &--font-xs {
        --route-number-font-size: #{calc(10px / $font-size-root) * 1rem} !important;
    }

    &--font-xxs {
        --route-number-font-size: #{calc(9.5px / $font-size-root) * 1rem} !important;
    }
}
</style>
